import React, { useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import { Input } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { designActions } from '../../../actions';
import { DesignContext } from '../../../containers/GraphicBuilder/GraphicBuilder';
import { useTranslation } from 'react-i18next';
import './AdjustPanel.css';

const convertBase = num => {
  return {
    from: baseFrom => {
      return {
        to: baseTo => {
          return parseInt(num, baseFrom).toString(baseTo);
        },
      };
    },
  };
};

const hex2dec = num => {
  return (
    parseInt(
      convertBase(num)
        .from(16)
        .to(10),
    ) - 100
  );
};

const dec2hex = num => {
  let convert = convertBase(parseInt(num) + 100)
    .from(10)
    .to(16);
  if (convert.length < 2) {
    convert = '0' + convert;
  }
  return convert;
};

const AdjustPanel = props => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { activePageRef } = useContext(DesignContext);
  const selectedEntity = useSelector(state => state.design.selectedEntity);
  const entity = useSelector(state => state.design.entitiesState[selectedEntity.id]);
  const background = useSelector(state => state.design.pages[activePageRef.current].background);
  const [codeError, setCodeError] = useState();

  const text = {
    brightness: t('brightness'),
    contrast: t('contrast'),
    saturation: t('saturation'),
    tint: t('tint'),
    blur: t('blur'),
    vignette: t('vignette'),
    xProcess: 'X-Process',
    intensity: 'Intensity',
  };

  useEffect(() => {
    document.querySelector('.wpCover').classList.add('active');
    createFilterCode();
    return () => {
      document.querySelector('.wpCover').classList.remove('active');
    };
  }, []);

  let entityFilterCode;
  if (selectedEntity.id.toString().includes('-')) {
    if (background.media.type === 'Image') {
      entityFilterCode = _.get(background, 'media.filter.code', '6464646464646432');
    }
  } else {
    if (entity.type === 'Image') {
      entityFilterCode = _.get(entity, 'style.filter.code', '6464646464646432');
    } else {
      entityFilterCode = _.get(entity, 'data.medias.style.filter.code', '6464646464646432');
    }
  }

  let adjustValuesHex = entityFilterCode.match(/.{2}/g);
  let adjustValues = _.map(adjustValuesHex, hex2dec);

  const adjustNames = [
    'brightness',
    'contrast',
    'saturation',
    'tint',
    'blur',
    'vignette',
    'xProcess',
    'intensity',
  ];
  let adjustments = _.zipObject(adjustNames, adjustValues);
  const [brightness, setBrightness] = useState(adjustments.brightness);
  const [contrast, setContrast] = useState(adjustments.contrast);
  const [saturation, setSaturation] = useState(adjustments.saturation);
  const [tint, setTint] = useState(adjustments.tint);
  const [blur, setBlur] = useState(adjustments.blur);
  const [vignette, setVignette] = useState(adjustments.vignette);
  const [xProcess, setXProcess] = useState(adjustments.xProcess);
  const [filterCode, setFilterCode] = useState();

  const handleChange = (e, { name, value }) => {
    if (name === 'brightness') {
      setBrightness(value);
    }
    if (name === 'contrast') {
      setContrast(value);
    }
    if (name === 'saturation') {
      setSaturation(value);
    }
    if (name === 'tint') {
      setTint(value);
    }
    if (name === 'blur') {
      setBlur(value);
    }
    if (name === 'xProcess') {
      setXProcess(value);
    }

    if (name === 'vignette') {
      setVignette(value);
    }
    createFilterCode(1);
  };

  const createFilterCode = save => {
    const code =
      dec2hex(brightness) +
      dec2hex(contrast) +
      dec2hex(saturation) +
      dec2hex(tint) +
      dec2hex(blur) +
      dec2hex(vignette) +
      dec2hex(xProcess) +
      '32';

    setFilterCode(code);

    save && saveCode(code);
  };

  const saveCode = code => {
    if (selectedEntity.type === 'Image' || selectedEntity.type === 'ImageBackground') {
      const filter = { code: code };
      dispatch(designActions.changeImageFilter(activePageRef.current, selectedEntity.id, filter));
    } else {
      const filter = { medias: { style: { filter: { code: code } } } };
      dispatch(designActions.changeFrameData(activePageRef.current, selectedEntity.id, filter));
    }
  };

  const handleClose = () => {
    dispatch(designActions.showFilterPanel(false));
  };

  const changeFilterCode = value => {
    setFilterCode(value);
    if (value.length < 16) {
      setCodeError('danger');
    } else {
      adjustValuesHex = value.match(/.{2}/g);
      adjustValues = _.map(adjustValuesHex, hex2dec);
      adjustments = _.zipObject(adjustNames, adjustValues);
      setBrightness(adjustments.brightness);
      setContrast(adjustments.contrast);
      setSaturation(adjustments.saturation);
      setTint(adjustments.tint);
      setBlur(adjustments.blur);
      setVignette(adjustments.vignette);
      setXProcess(adjustments.xProcess);

      setCodeError('');
      saveCode(value);
    }
  };

  return (
    <>
      <div className="panelsCloseStrip" onClick={handleClose}></div>
      <div className="adjustPanelHolder">
        <div className="header">
          <span className="closeIcon btn left" onClick={handleClose}></span>
        </div>
        <div className="holder content">
          <div className="bottomShade"></div>
          <div title="Brightness" className="slider brightness">
            <span className="title">{text.brightness}</span>
            <Input
              label={brightness}
              min={-100}
              max={100}
              name={'brightness'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={brightness}
            />
          </div>
          <div title="Contrast" className="slider contrast">
            <span className="title">{text.contrast}</span>
            <Input
              label={contrast}
              min={-100}
              max={100}
              name={'contrast'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={contrast}
            />
          </div>
          <div title="Saturation" className="slider saturation">
            <span className="title">{text.saturation}</span>
            <Input
              label={saturation}
              min={-100}
              max={100}
              name={'saturation'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={saturation}
            />
          </div>
          <div title="Tint" className="slider tint">
            <span className="title">{text.tint}</span>
            <Input
              label={tint}
              min={-100}
              max={100}
              name={'tint'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={tint}
            />
          </div>
          <div title="Blur" className="slider blur">
            <span className="title">{text.blur}</span>
            <Input
              label={blur}
              min={-100}
              max={100}
              name={'blur'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={blur}
            />
          </div>
          <div title="xProcess" className="slider xProcess">
            <span className="title">{text.xProcess}</span>
            <Input
              label={xProcess}
              min={0}
              max={100}
              name={'xProcess'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={xProcess}
            />
          </div>
          <div title="Vignette" className="slider vignette">
            <span className="title">{text.vignette}</span>
            <Input
              label={vignette}
              min={-100}
              max={100}
              name={'vignette'}
              onChange={handleChange}
              // onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={vignette}
            />
          </div>
        </div>
        <div className="footer">
          <span className="">{t('filterCode')}</span>
          <input
            className={`${codeError}`}
            type="text"
            value={filterCode}
            onChange={e => changeFilterCode(e.target.value)}
            maxLength="16"
          ></input>
        </div>
      </div>
    </>
  );
};

export default AdjustPanel;
